import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


interface HeroSectionProps {
  className?: string;
  textColor?: string;
}

const HeroSection = ({ className = '', textColor = '#ffffff' }: HeroSectionProps) => {
  return (
    <section className={`relative overflow-hidden ${className}`}>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="text-center space-y-10">
          {/* WeSkill Logo */}
          <div className="flex justify-center mb-8">
            









          </div>

          {/* Premium Badge */}
          



          
          {/* Main Headline */}
          <h1 className="font-headline text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight max-w-5xl mx-auto"
              style={{ color: textColor }}>
            Build, Learn & Grow Skills with{' '}
            <span className="text-accent">AI-Powered Mentorship</span>
          </h1>
          
          {/* Subheadline */}
          <p className="text-xl sm:text-2xl leading-relaxed max-w-3xl mx-auto font-light"
             style={{ color: textColor, opacity: 0.9 }}>
            Transform your career with expert-led courses, personalized AI guidance, and a thriving community of learners
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center pt-8">
            <Link
              href="/course-catalog"
              className="group inline-flex items-center justify-center space-x-3 bg-white hover:bg-gray-50 font-cta font-bold px-10 py-5 rounded-xl transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105 min-w-[200px]"
              style={{ color: '#000000' }}>
              <span className="text-lg">Get Started</span>
              <Icon name="ArrowRightIcon" size={24} variant="solid" className="group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <Link
              href="/course-catalog"
              className="group inline-flex items-center justify-center space-x-3 bg-white/10 backdrop-blur-sm hover:bg-white/20 font-cta font-bold px-10 py-5 rounded-xl border-2 transition-all duration-300 min-w-[200px]"
              style={{ color: textColor, borderColor: `${textColor}4D` }}>
              <Icon name="VideoCameraIcon" size={24} variant="solid" />
              <span className="text-lg">Book a Demo</span>
            </Link>
          </div>
          
          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-8 pt-12">
            <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
              <Icon name="UserGroupIcon" size={28} variant="solid" className="text-accent" />
              <div className="text-left">
                <p className="text-2xl font-bold" style={{ color: textColor }}>50,000+</p>
                <p className="text-sm" style={{ color: textColor, opacity: 0.8 }}>Active Learners</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
              <Icon name="StarIcon" size={28} variant="solid" className="text-accent" />
              <div className="text-left">
                <p className="text-2xl font-bold" style={{ color: textColor }}>4.9/5</p>
                <p className="text-sm" style={{ color: textColor, opacity: 0.8 }}>Average Rating</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
              <Icon name="AcademicCapIcon" size={28} variant="solid" className="text-accent" />
              <div className="text-left">
                <p className="text-2xl font-bold" style={{ color: textColor }}>500+</p>
                <p className="text-sm" style={{ color: textColor, opacity: 0.8 }}>Expert Courses</p>
              </div>
            </div>
          </div>

          {/* Feature Highlights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-16 max-w-5xl mx-auto">
            <div className="bg-white/5 backdrop-blur-md rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center">
                  <Icon name="CpuChipIcon" size={32} variant="solid" className="text-accent" />
                </div>
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: textColor }}>AI-Powered Learning</h3>
              <p className="text-sm" style={{ color: textColor, opacity: 0.7 }}>Personalized learning paths tailored to your goals and pace</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-md rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center">
                  <Icon name="VideoCameraIcon" size={32} variant="solid" className="text-accent" />
                </div>
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: textColor }}>Live Interactive Classes</h3>
              <p className="text-sm" style={{ color: textColor, opacity: 0.7 }}>Real-time sessions with expert instructors and peer collaboration</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-md rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center">
                  <Icon name="TrophyIcon" size={32} variant="solid" className="text-accent" />
                </div>
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: textColor }}>Industry Recognition</h3>
              <p className="text-sm" style={{ color: textColor, opacity: 0.7 }}>Earn certificates valued by top companies worldwide</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Decorative Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background to-transparent"></div>
    </section>
  );
};

export default HeroSection;